import React, { useRef, useState } from 'react';
import { Camera, User } from 'lucide-react';
import { VipBadge } from './ui/VipBadge';

interface ProfileAvatarProps {
  currentUrl: string | null;
  vipTier?: number | string | null;
  vipExpireAt?: string | Date | null;
  onImageChange?: (base64: string) => void;
  onOpenAvatarModal?: () => void;
}

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  currentUrl,
  vipTier,
  vipExpireAt,
  onImageChange,
  onOpenAvatarModal
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleClick = () => {
    if (onOpenAvatarModal) {
      onOpenAvatarModal();
    } else {
      inputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      if (onImageChange) onImageChange(result);
    };
    reader.readAsDataURL(file);
  };

  const tierNum = typeof vipTier === 'number' ? vipTier : (vipTier ? Number(vipTier) : 0);

  // Viền Avatar phong cách VIP/SVIP
  const avatarRingClass = tierNum === 2
    ? 'ring-4 ring-rose-500 ring-offset-2 ring-offset-slate-50 shadow-rose-500/20'
    : tierNum === 1
    ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-slate-50 shadow-amber-500/20'
    : 'ring-4 ring-white';

  return (
    <div className="flex flex-col items-center">
      <div 
        className="relative group cursor-pointer"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={handleClick}
      >
        <div className={`w-32 h-32 rounded-full overflow-hidden shadow-lg bg-slate-100 flex items-center justify-center transition-all ${avatarRingClass}`}>
          {currentUrl ? (
            <img
              src={currentUrl}
              alt="Profile"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              referrerPolicy="no-referrer"
            />
          ) : (
            <User size={48} className="text-slate-400" />
          )}
        </div>

        {/* Overlay khi Hover */}
        <div className={`absolute inset-0 bg-black/30 rounded-full flex items-center justify-center transition-opacity duration-200 ${isHovering ? 'opacity-100' : 'opacity-0'}`}>
          <Camera className="text-white w-8 h-8 drop-shadow-md" />
        </div>

        {/* Badge VIP/SVIP kế bên/đính kèm dưới Avatar */}
        {tierNum > 0 && (
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-10">
            <VipBadge
              vipTier={tierNum}
              vipExpireAt={vipExpireAt}
              size="sm"
              className="shadow-md"
            />
          </div>
        )}
        
        {!onOpenAvatarModal && (
          <input 
            type="file" 
            ref={inputRef} 
            className="hidden" 
            accept="image/*"
            onChange={handleFileChange}
          />
        )}
      </div>

      <button
        type="button"
        onClick={handleClick}
        className="mt-3.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium hover:underline transition-colors focus:outline-none"
      >
        Nhấn để thay đổi ảnh
      </button>
    </div>
  );
};

export default ProfileAvatar;
